<script setup>
import { useI18n } from 'vue-i18n';
import { ref, watch, nextTick } from 'vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';

const props = defineProps({
  messages: {
    type: Array,
    required: true,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const messageContainer = ref(null);

const { t } = useI18n();
const { formatMessage } = useMessageFormatter();

const isUserMessage = sender => sender === 'user';

const getMessageAlignment = sender =>
  isUserMessage(sender) ? 'justify-end' : 'justify-start';

const getMessageDirection = sender =>
  isUserMessage(sender) ? 'flex-row-reverse' : 'flex-row';

const getAvatarName = sender =>
  isUserMessage(sender)
    ? t('CAPTAIN.PLAYGROUND.USER')
    : t('CAPTAIN.PLAYGROUND.ASSISTANT');

const getMessageStyle = sender =>
  isUserMessage(sender)
    ? 'bg-n-solid-blue text-n-slate-12 rounded-br-sm rounded-bl-xl rounded-t-xl'
    : 'bg-n-solid-iris text-n-slate-12 rounded-bl-sm rounded-br-xl rounded-t-xl';

const systemNotices = {
  handoff: {
    title: t('CAPTAIN.PLAYGROUND.HANDOFF.TITLE'),
    description: t('CAPTAIN.PLAYGROUND.HANDOFF.DESCRIPTION'),
  },
  auto_resolved: {
    title: t('CAPTAIN.PLAYGROUND.AUTO_RESOLVED.TITLE'),
    description: t('CAPTAIN.PLAYGROUND.AUTO_RESOLVED.DESCRIPTION'),
  },
};

const formatJson = value => JSON.stringify(value, null, 2);

const scrollToBottom = async () => {
  await nextTick();
  if (messageContainer.value) {
    messageContainer.value.scrollTop = messageContainer.value.scrollHeight;
  }
};

watch(() => props.messages.length, scrollToBottom);
</script>

<template>
  <div
    ref="messageContainer"
    class="flex-1 overflow-y-auto mb-4 px-6 space-y-6"
  >
    <template v-for="(message, index) in messages" :key="index">
      <div
        v-if="message.sender === 'system'"
        class="flex flex-col items-center gap-1 px-4 py-3 mx-auto text-center border rounded-lg max-w-md border-n-amber-6 bg-n-amber-3 text-n-amber-12"
      >
        <span class="text-sm font-medium">
          {{ systemNotices[message.type]?.title }}
        </span>
        <span class="text-xs opacity-80">
          {{ systemNotices[message.type]?.description }}
        </span>
      </div>
      <div v-else class="flex" :class="getMessageAlignment(message.sender)">
        <div
          class="flex items-end gap-1.5 max-w-[90%] md:max-w-[60%]"
          :class="getMessageDirection(message.sender)"
        >
          <Avatar
            :name="getAvatarName(message.sender)"
            rounded-full
            :size="24"
            class="shrink-0"
          />
          <div
            class="px-4 py-3 text-sm [overflow-wrap:break-word]"
            :class="getMessageStyle(message.sender)"
          >
            <audio
              v-if="message.attachmentPreviewUrl && message.attachmentIsAudio"
              :src="message.attachmentPreviewUrl"
              controls
              class="mb-2 max-w-full"
            />
            <img
              v-else-if="message.attachmentPreviewUrl"
              :src="message.attachmentPreviewUrl"
              class="mb-2 rounded max-h-40"
            />
            <p
              v-if="message.transcription"
              class="mb-2 text-xs italic opacity-80"
            >
              {{
                t('CAPTAIN.PLAYGROUND.AUDIO.TRANSCRIPTION', {
                  text: message.transcription,
                })
              }}
            </p>
            <details
              v-for="(call, callIndex) in message.toolCalls"
              :key="callIndex"
              class="mb-3 text-xs border rounded-lg border-n-slate-5 bg-n-alpha-black2"
            >
              <summary class="px-3 py-2 font-medium cursor-pointer">
                {{
                  t('CAPTAIN.PLAYGROUND.TOOL_CALLS.TITLE', {
                    title: call.title,
                  })
                }}
              </summary>
              <div class="flex flex-col gap-2 px-3 pb-3 font-mono break-all">
                <div>
                  <b>{{ t('CAPTAIN.PLAYGROUND.TOOL_CALLS.EVENT') }}:</b>
                  {{ call.tool }}
                </div>
                <div>
                  <b>{{ t('CAPTAIN.PLAYGROUND.TOOL_CALLS.REQUEST') }}:</b>
                  {{ call.http_method }} {{ call.url }}
                </div>
                <div>
                  <b>{{ t('CAPTAIN.PLAYGROUND.TOOL_CALLS.AUTH') }}:</b>
                  {{ call.auth_type }}
                </div>
                <div v-if="call.headers">
                  <b>{{ t('CAPTAIN.PLAYGROUND.TOOL_CALLS.HEADERS') }}:</b>
                  <pre class="m-0 whitespace-pre-wrap">{{
                    formatJson(call.headers)
                  }}</pre>
                </div>
                <div>
                  <b>{{ t('CAPTAIN.PLAYGROUND.TOOL_CALLS.PARAMS') }}:</b>
                  <pre class="m-0 whitespace-pre-wrap">{{
                    formatJson(call.params)
                  }}</pre>
                </div>
                <div v-if="call.body">
                  <b>{{ t('CAPTAIN.PLAYGROUND.TOOL_CALLS.BODY') }}:</b>
                  <pre class="m-0 whitespace-pre-wrap">{{ call.body }}</pre>
                </div>
                <div v-if="call.status">
                  <b>{{ t('CAPTAIN.PLAYGROUND.TOOL_CALLS.STATUS') }}:</b>
                  {{ call.status }}
                </div>
                <div v-if="call.response_body">
                  <b>{{ t('CAPTAIN.PLAYGROUND.TOOL_CALLS.RESPONSE') }}:</b>
                  <pre class="m-0 whitespace-pre-wrap">{{
                    call.response_body
                  }}</pre>
                </div>
                <div v-if="call.blocked" class="font-semibold text-n-ruby-11">
                  {{ t('CAPTAIN.PLAYGROUND.TOOL_CALLS.BLOCKED') }}
                </div>
                <div v-if="call.error" class="text-n-ruby-11">
                  <b>{{ t('CAPTAIN.PLAYGROUND.TOOL_CALLS.ERROR') }}:</b>
                  {{ call.error }}
                </div>
                <div>
                  <b>{{ t('CAPTAIN.PLAYGROUND.TOOL_CALLS.DURATION') }}:</b>
                  {{ `${call.duration_ms} ms` }}
                </div>
              </div>
            </details>
            <div v-html="formatMessage(message.content)" />
            <div
              v-if="message.sharedContent"
              class="flex flex-col gap-2 pt-3 mt-3 border-t border-n-slate-4"
            >
              <span class="text-xs opacity-80">
                {{
                  message.sharedContent.type === 'attachments'
                    ? t('CAPTAIN.PLAYGROUND.SHARED_CONTENT.ATTACHMENT_LABEL')
                    : t('CAPTAIN.PLAYGROUND.SHARED_CONTENT.URL_LABEL')
                }}
              </span>

              <template v-if="message.sharedContent.type === 'attachments'">
                <template
                  v-for="file in message.sharedContent.files"
                  :key="file.url"
                >
                  <img
                    v-if="file.content_type?.startsWith('image/')"
                    :src="file.url"
                    class="rounded max-h-40"
                  />
                  <a
                    v-else
                    :href="file.url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-flex gap-1.5 items-center text-xs underline"
                  >
                    <i class="i-lucide-paperclip size-3.5" />
                    {{ file.filename }}
                  </a>
                </template>
              </template>
              <template v-else>
                <a
                  v-if="message.sharedContent.action_url"
                  :href="message.sharedContent.action_url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-xs underline break-all"
                >
                  {{ message.sharedContent.action_url }}
                </a>
                <a
                  v-if="message.sharedContent.media_url"
                  :href="message.sharedContent.media_url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-xs underline break-all"
                >
                  {{ message.sharedContent.media_url }}
                </a>
              </template>
            </div>
          </div>
        </div>
      </div>
    </template>
    <div v-if="isLoading" class="flex justify-start">
      <div class="flex items-start gap-1.5">
        <Avatar :name="getAvatarName('assistant')" rounded-full :size="24" />
        <div
          class="max-w-sm rounded-lg p-3 text-sm bg-n-solid-iris text-n-slate-12"
        >
          <div class="flex gap-1">
            <div class="w-2 h-2 rounded-full bg-n-iris-10 animate-bounce" />
            <div
              class="w-2 h-2 rounded-full bg-n-iris-10 animate-bounce [animation-delay:0.2s]"
            />
            <div
              class="w-2 h-2 rounded-full bg-n-iris-10 animate-bounce [animation-delay:0.4s]"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
